import { useEffect, useRef, useState } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Play, Apple, Users, Trophy, Flame } from 'lucide-react';
import { api } from '../../lib/api';

const LINES = ["INDIA'S ULTIMATE", 'BGMI & FREE FIRE', 'ESPORTS PLATFORM'];

function CountUp({ value }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const dur = 1400;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);
  return <span ref={ref}>{Number(n || 0).toLocaleString('en-IN')}</span>;
}

export default function Hero({ settings }) {
  const [stats, setStats] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const watermarkY = useTransform(scrollYProgress, [0, 1], [0, -90]);

  useEffect(() => {
    api.stats().then(setStats).catch(() => {});
  }, []);

  const [videoId, setVideoId] = useState(null);
  useEffect(() => {
    if (settings == null) return;
    const extract = (u) => {
      if (!u) return null;
      const t = u.trim();
      const m = t.match(/(?:youtube\.com\/(?:live\/|watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/);
      if (m) return m[1];
      return /^[\w-]{11}$/.test(t) ? t : null;
    };
    const pinned = extract(settings?.hero_video_url);
    if (pinned) {
      setVideoId(pinned);
      return;
    }
    let cancelled = false;
    api
      .streams()
      .then((d) => {
        if (cancelled) return;
        const s = (d.live && d.live[0]) || (d.past && d.past[0]);
        const id = extract(s?.youtube_url);
        if (id) setVideoId(id);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [settings]);

  useEffect(() => {
    if (!videoId) return;
    let player;
    const mount = () => {
      try {
        player = new window.YT.Player('hero-yt-player', {
          videoId,
          width: '100%',
          height: '100%',
          playerVars: {
            autoplay: 1,
            mute: 1,
            loop: 1,
            playlist: videoId,
            controls: 0,
            rel: 0,
            modestbranding: 1,
            playsinline: 1,
            disablekb: 1,
            fs: 0,
            iv_load_policy: 3,
          },
          events: {
            onReady: (e) => {
              e.target.mute();
              e.target.playVideo();
            },
            onError: () => setVideoId((cur) => (cur === videoId ? null : cur)),
          },
        });
      } catch {
        setVideoId((cur) => (cur === videoId ? null : cur));
      }
    };
    if (window.YT && window.YT.Player) {
      mount();
    } else {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (prev) prev();
        mount();
      };
      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(tag);
      }
    }
    return () => {
      try {
        player?.destroy();
      } catch {}
    };
  }, [videoId]);

  const android = settings?.android_download_url;
  const ios = settings?.ios_download_url;

  const statCards = [
    { key: 'total_players', label: 'Total Players', icon: Users },
    { key: 'tournaments_completed', label: 'Tournaments Completed', icon: Trophy },
    { key: 'ongoing_tournaments', label: 'Ongoing Tournaments', icon: Flame },
  ];

  return (
    <section ref={heroRef} className="relative overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 hero-grid" aria-hidden="true" />
      {videoId && (
        <div className="absolute inset-0 overflow-hidden" aria-hidden="true" data-testid="hero-video-bg">
          <div className="hero-yt-wrap absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[max(100%,177.78vh)] h-[max(100%,56.25vw)] pointer-events-none opacity-25 saturate-[0.6]">
            <div key={videoId} id="hero-yt-player" />
          </div>
          <div className="absolute inset-0 bg-[#0d0d0d]/65" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0d]/50 via-transparent to-[#0d0d0d]" />
        </div>
      )}
      <div className="absolute inset-0 red-radial" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-16 sm:pt-20 sm:pb-24">
        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-8 items-center">
          <div>
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 border border-[#e8173a]/40 bg-[#e8173a]/10 rounded-full px-4 py-1.5 mb-8"
          data-testid="hero-badge"
        >
          <span className="w-2 h-2 rounded-full bg-[#e8173a] animate-pulse" />
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#ff5c77]">
            Season Tournaments Live Now
          </span>
        </motion.div>

        <h1 className="font-display text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.02]" data-testid="hero-headline">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? 'text-[#e8173a]' : ''}`}
                initial={{ y: '110%' }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-6 text-base sm:text-lg text-[#9e9e9e] font-medium tracking-wide"
          data-testid="hero-subtext"
        >
          Compete. <span className="text-white">Win.</span>{' '}
          <span className="text-[#00c853] font-semibold">Earn Real Money.</span>
        </motion.p>

        {(android || ios) && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-10 flex flex-col sm:flex-row gap-4 max-w-xl"
          >
            {android && (
              <a
                data-testid="hero-google-play-button"
                href={android}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-3 bg-[#e8173a] hover:bg-[#c0102e] text-white font-extrabold uppercase tracking-wider text-sm px-8 py-4 rounded-xl transition-all duration-300 hover:shadow-[0_20px_50px_-15px_rgba(232,23,58,0.7)] hover:-translate-y-0.5 active:scale-95"
              >
                <Play className="w-5 h-5 fill-current" />
                Google Play
              </a>
            )}
            {ios && (
              <a
                data-testid="hero-app-store-button"
                href={ios}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-3 bg-transparent border-2 border-[#e8173a] text-[#e8173a] hover:bg-[#e8173a]/10 font-extrabold uppercase tracking-wider text-sm px-8 py-4 rounded-xl transition-all duration-300 hover:-translate-y-0.5 active:scale-95"
              >
                <Apple className="w-5 h-5 fill-current" />
                App Store
              </a>
            )}
          </motion.div>
        )}
          </div>

          <motion.div
            style={{ y: watermarkY }}
            className="relative flex justify-center lg:justify-end"
            data-testid="hero-logo-art"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.7, rotate: -8 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 1.2, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            >
              <motion.div
                animate={{ y: [0, -14, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                className="relative"
              >
                <div className="absolute inset-0 bg-[#e8173a]/30 blur-[90px] rounded-full scale-90" aria-hidden="true" />
                <img
                  src="/kroy-logo.jpeg"
                  alt="KROY Esports — Kannadiga Royals"
                  className="relative w-56 sm:w-72 lg:w-[400px] mix-blend-screen [mask-image:radial-gradient(closest-side,black_62%,transparent_99%)]"
                />
              </motion.div>
            </motion.div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl"
          data-testid="hero-stats"
        >
          {statCards.map(({ key, label, icon: Icon }) => (
            <div
              key={key}
              data-testid={`hero-stat-${key.replace(/_/g, '-')}`}
              className="card-surface backdrop-blur-sm bg-[#1a1f2e]/80 p-5 flex items-center gap-4"
            >
              <span className="w-10 h-10 rounded-lg bg-[#e8173a]/10 border border-[#e8173a]/30 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 text-[#e8173a]" />
              </span>
              <div>
                <p className="text-2xl font-black italic text-white leading-none">
                  {stats ? <CountUp value={stats[key] || 0} /> : '—'}
                </p>
                <p className="mt-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8a8a8a]">{label}</p>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
